import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { TrendingUp, CheckCircle2, ShoppingBag } from "lucide-react";

const FLOW = [
  { m: "Jan", projetada: 118, realizada: 102 },
  { m: "Fev", projetada: 132, realizada: 121 },
  { m: "Mar", projetada: 141, realizada: 138 },
  { m: "Abr", projetada: 156, realizada: 140 },
  { m: "Mai", projetada: 171, realizada: 158 },
  { m: "Jun", projetada: 184, realizada: 162 },
];

const LOTES = [
  { lote: "L94", margem: 31 },
  { lote: "L97", margem: 44 },
  { lote: "L102", margem: 52 },
  { lote: "L108", margem: 58 },
  { lote: "L111", margem: 47 },
];

const DONUT = [
  { name: "Vendido c/ margem", value: 58 },
  { name: "Em estoque", value: 27 },
  { name: "Remarcado", value: 15 },
];
const DONUT_COLORS = ["#2E7CF6", "#1A64FF", "#7DD3FC"];

const SALES_POOL = [
  { lote: "L108", item: "Vestido midi", valor: 189.9 },
  { lote: "L102", item: "Blazer de linho", valor: 249.9 },
  { lote: "L97", item: "Bolsa de couro", valor: 219.0 },
  { lote: "L111", item: "Calça wide leg", valor: 159.9 },
  { lote: "L108", item: "Camisa de seda", valor: 139.9 },
  { lote: "L94", item: "Saia plissada", valor: 119.9 },
  { lote: "L102", item: "Trench coat", valor: 329.9 },
  { lote: "L111", item: "Tricô oversized", valor: 149.9 },
  { lote: "L97", item: "Scarpin", valor: 199.9 },
  { lote: "L94", item: "Cinto de couro", valor: 79.9 },
];

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v);
const fmtBRL2 = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const KPIS = [
  { label: "Receita Projetada Atualizada", value: "R$ 184,5 mil", delta: "+14,2%" },
  { label: "Receita Realizada", value: "R$ 162,4 mil", delta: "+9,8%" },
  { label: "Margem de Contribuição", value: "48,6%", delta: "+3,4 p.p." },
  { label: "Resultado Operacional", value: "R$ 41,2 mil", delta: "+22,1%" },
];

const tooltipStyle = { contentStyle: { background: "rgba(6,13,31,0.95)", border: "1px solid rgba(125,211,252,0.25)", borderRadius: 10, fontSize: 11, fontFamily: "JetBrains Mono, monospace", color: "#fff" }, labelStyle: { color: "#7DD3FC" }, itemStyle: { color: "#fff" }, cursor: { stroke: "rgba(125,211,252,0.3)" } };

export const DashboardMockup = ({ compact = false, live = false }) => {
  const [realized, setRealized] = useState(162380);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    if (!live) return;
    const tick = () => {
      const s = SALES_POOL[Math.floor(Math.random() * SALES_POOL.length)];
      setSales((prev) => [...prev.slice(-2), { ...s, id: `${Date.now()}-${Math.random()}` }]);
      setRealized((r) => r + s.valor);
    };
    const t0 = setTimeout(tick, 2000);
    const t = setInterval(tick, 4200);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, [live]);

  const lastSale = sales[sales.length - 1];

  return (
    <div data-testid="dashboard-mockup" className="glass-card relative overflow-hidden rounded-2xl shadow-[0_40px_120px_-20px_rgba(26,100,255,0.35)]">
      <div className="flex items-center gap-2 border-b border-white/[0.07] px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-tria-blue/70" />
        <span className="ml-3 font-mono text-[10px] tracking-widest text-tria-subtle">TRIA · PAINEL GERENCIAL</span>
        <span className="ml-auto hidden items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[9.5px] font-semibold text-emerald-300 sm:flex">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
          AO VIVO
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 sm:p-5">
        {KPIS.map((k) => {
          const isLiveRealized = live && k.label === "Receita Realizada";
          return (
            <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3">
              <p className="text-[10px] font-medium leading-tight text-tria-subtle">{k.label}</p>
              {isLiveRealized ? (
                <motion.p
                  key={realized}
                  initial={{ color: "#34D399" }}
                  animate={{ color: "#FFFFFF" }}
                  transition={{ duration: 1.4 }}
                  className="mt-1.5 font-mono text-sm font-bold sm:text-base"
                  data-testid="kpi-receita-realizada-live"
                >
                  {fmtBRL(realized)}
                </motion.p>
              ) : (
                <p className="mt-1.5 font-mono text-sm font-bold text-white sm:text-base">{k.value}</p>
              )}
              <p className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-emerald-400">
                <TrendingUp className="h-3 w-3" /> {k.delta}
              </p>
            </div>
          );
        })}
      </div>

      <div className={`grid gap-3 px-4 pb-4 sm:px-5 sm:pb-5 ${compact ? "" : "lg:grid-cols-5"}`}>
        <div className={`rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 ${compact ? "" : "lg:col-span-3"}`}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold text-tria-mist">Receita projetada × realizada</p>
            <div className="flex items-center gap-3 font-mono text-[9px] text-tria-subtle">
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-tria-sky" />Projetada</span>
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-tria-vivid" />Realizada</span>
            </div>
          </div>
          <div className="h-[150px] sm:h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={FLOW} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="gProj" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7DD3FC" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#7DD3FC" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gReal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1A64FF" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#1A64FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="projetada" stroke="#7DD3FC" strokeWidth={2} fill="url(#gProj)" name="Projetada (R$ mil)" />
                <Area type="monotone" dataKey="realizada" stroke="#1A64FF" strokeWidth={2.5} fill="url(#gReal)" name="Realizada (R$ mil)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {!compact && (
          <>
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 lg:col-span-1">
              <p className="mb-1 text-[11px] font-semibold text-tria-mist">Margem por lote</p>
              <div className="h-[150px] sm:h-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={LOTES} margin={{ top: 8, right: 0, left: -26, bottom: 0 }}>
                    <XAxis dataKey="lote" tick={{ fill: "#64748B", fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip {...tooltipStyle} formatter={(v) => [`${v}%`, "Margem"]} />
                    <Bar dataKey="margem" radius={[4, 4, 0, 0]}>
                      {LOTES.map((_, i) => (
                        <Cell key={i} fill={i === 3 ? "#7DD3FC" : "#1A64FF"} fillOpacity={i === 3 ? 1 : 0.55 + i * 0.1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 lg:col-span-1">
              <p className="mb-1 text-[11px] font-semibold text-tria-mist">Status dos lotes</p>
              <div className="relative h-[120px] sm:h-[130px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={DONUT} innerRadius="62%" outerRadius="88%" paddingAngle={3} dataKey="value" stroke="none">
                      {DONUT.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-mono text-lg font-bold text-white">58%</span>
                  <span className="text-[9px] text-tria-subtle">com margem</span>
                </div>
              </div>
              <div className="mt-1 space-y-1">
                {DONUT.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-[9px] text-tria-subtle">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: DONUT_COLORS[i] }} />
                    {d.name}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="border-t border-white/[0.07] bg-gradient-to-r from-tria-vivid/10 to-transparent px-5 py-3">
        {live && lastSale && (
          <div data-testid="live-sale-ticker" className="mb-2 flex items-center gap-2 overflow-hidden">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <AnimatePresence mode="popLayout">
              <motion.p
                key={lastSale.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-1.5 truncate font-mono text-[10.5px] text-white"
              >
                <ShoppingBag className="h-3 w-3 shrink-0 text-tria-sky" />
                Venda registrada · {lastSale.item} — {lastSale.lote}
              </motion.p>
            </AnimatePresence>
            <motion.span
              key={`${lastSale.id}-v`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="ml-auto shrink-0 font-mono text-[10.5px] font-bold text-emerald-400"
            >
              +{fmtBRL2(lastSale.valor)}
            </motion.span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
          <p className="font-mono text-[10.5px] text-tria-mist">
            Ponto de equilíbrio <span className="font-bold text-white">R$ 88,2 mil</span> atingido no dia 14 — operação em lucro
          </p>
        </div>
      </div>
    </div>
  );
};
